/**
 * Supabase-based Search Service
 * Replaces Algolia with PostgreSQL full-text search capabilities
 */

import { supabase } from "../lib/supabase";
import { enhancedQuery } from "../lib/supabase";

// Search result interfaces
export interface SearchResult {
  id: string;
  title: string;
  description?: string;
  url: string;
  type: "Blog Post" | "Course" | "Prompt";
  score?: number; // Relevance score (0-100)
  published_at?: string;
  created_at?: string;
  slug?: string;
  excerpt?: string;
  meta_description?: string;
}

export interface SearchResponse {
  blog: SearchResult[];
  courses: SearchResult[];
  prompts: SearchResult[];
  total: number;
  query: string;
  searchTime: number;
}

export interface SearchOptions {
  limit?: number;
  offset?: number;
  types?: ("blog" | "course" | "prompt")[];
  sortBy?: "relevance" | "date" | "title";
}

/**
 * Main search function that searches across all content types
 */
export class SearchService {
  /**
   * Search across all content types
   */
  static async searchAll(
    query: string,
    options: SearchOptions = {},
  ): Promise<SearchResponse> {
    const startTime = performance.now();
    const { limit = 10, types = ["blog", "course", "prompt"] } = options;

    try {
      const searchPromises = [];
      const searchTypes = [];

      if (types.includes("blog")) {
        searchPromises.push(
          this.searchBlogPosts(query, { limit: Math.ceil(limit * 0.6) }),
        );
        searchTypes.push("blog");
      }

      if (types.includes("course")) {
        searchPromises.push(
          this.searchCourses(query, { limit: Math.ceil(limit * 0.2) }),
        );
        searchTypes.push("course");
      }

      if (types.includes("prompt")) {
        searchPromises.push(
          this.searchPrompts(query, { limit: Math.ceil(limit * 0.2) }),
        );
        searchTypes.push("prompt");
      }

      const results = await Promise.all(searchPromises);

      // Initialize result object
      const searchResponse = {
        blog: [],
        courses: [],
        prompts: [],
        total: 0,
        query,
        searchTime: 0,
      };

      // Assign results based on the order of searchTypes
      searchTypes.forEach((type, index) => {
        if (type === "blog") {
          searchResponse.blog = results[index] || [];
        } else if (type === "course") {
          searchResponse.courses = results[index] || [];
        } else if (type === "prompt") {
          searchResponse.prompts = results[index] || [];
        }
      });

      // Calculate total
      searchResponse.total =
        searchResponse.blog.length +
        searchResponse.courses.length +
        searchResponse.prompts.length;

      searchResponse.searchTime = performance.now() - startTime;

      return searchResponse;
    } catch (error) {
      console.error("❌ SearchService.searchAll error:", error);
      return {
        blog: [],
        courses: [],
        prompts: [],
        total: 0,
        query,
        searchTime: 0,
      };
    }
  }

  /**
   * Search blog posts using PostgreSQL full-text search
   */
  static async searchBlogPosts(
    query: string,
    options: { limit?: number } = {},
  ): Promise<SearchResult[]> {
    const { limit = 6 } = options;

    try {
      // Try full-text search first (if indexes exist)
      try {
        const { data, error } = await enhancedQuery(
          async (signal) =>
            supabase
              .from("blog_posts")
              .select(
                `
              id,
              title,
              slug,
              excerpt,
              meta_description,
              content,
              published_at,
              created_at,
              blog_categories(name, slug)
            `,
              )
              .textSearch("title_content", query, {
                type: "websearch",
                config: "english",
              })
              .eq("is_published", true)
              .order("published_at", { ascending: false })
              .limit(limit)
              .abortSignal(signal),
          { cacheKey: `blog_search_${query}_${limit}` },
        );

        if (error) throw error;

        return (data || []).map((post: any) => ({
          id: post.id,
          title: post.title,
          description: post.excerpt || post.meta_description,
          url: `/blog/${post.slug}`,
          type: "Blog Post" as const,
          published_at: post.published_at,
          created_at: post.created_at,
          slug: post.slug,
          excerpt: post.excerpt,
          meta_description: post.meta_description,
        }));
      } catch (fullTextError) {
        console.log(
          "Full-text search failed, falling back to basic search:",
          fullTextError,
        );

        // Fallback to basic ILIKE search
        const { data, error } = await enhancedQuery(
          async (signal) =>
            supabase
              .from("blog_posts")
              .select(
                `
              id,
              title,
              slug,
              excerpt,
              meta_description,
              content,
              published_at,
              created_at,
              blog_categories(name, slug)
            `,
              )
              .or(
                `title.ilike.%${query}%,excerpt.ilike.%${query}%,meta_description.ilike.%${query}%`,
              )
              .eq("is_published", true)
              .order("published_at", { ascending: false })
              .limit(limit)
              .abortSignal(signal),
          { cacheKey: `blog_search_basic_${query}_${limit}` },
        );

        if (error) throw error;

        return (data || []).map((post: any) => ({
          id: post.id,
          title: post.title,
          description: post.excerpt || post.meta_description,
          url: `/blog/${post.slug}`,
          type: "Blog Post" as const,
          published_at: post.published_at,
          created_at: post.created_at,
          slug: post.slug,
          excerpt: post.excerpt,
          meta_description: post.meta_description,
        }));
      }
    } catch (error) {
      console.error("Blog search error:", error);
      return [];
    }
  }

  /**
   * Search courses using PostgreSQL full-text search
   */
  static async searchCourses(
    query: string,
    options: { limit?: number } = {},
  ): Promise<SearchResult[]> {
    const { limit = 2 } = options;

    try {
      // Try full-text search first (if indexes exist)
      try {
        const { data, error } = await enhancedQuery(
          async (signal) =>
            supabase
              .from("courses")
              .select(
                `
              id,
              title,
              slug,
              description,
              short_description,
              created_at,
              course_categories(name, slug)
            `,
              )
              .textSearch("title_description", query, {
                type: "websearch",
                config: "english",
              })
              // .eq('is_active', true) // Commented out as column may not exist
              .order("created_at", { ascending: false })
              .limit(limit)
              .abortSignal(signal),
          { cacheKey: `course_search_${query}_${limit}` },
        );

        if (error) throw error;

        return (data || []).map((course: any) => ({
          id: course.id,
          title: course.title,
          description: course.short_description || course.description,
          url: `/course/${course.slug}`,
          type: "Course" as const,
          created_at: course.created_at,
          slug: course.slug,
        }));
      } catch (fullTextError) {
        console.log(
          "Full-text search failed for courses, falling back to basic search:",
          fullTextError,
        );

        // Fallback to basic ILIKE search
        const { data, error } = await enhancedQuery(
          async (signal) =>
            supabase
              .from("courses")
              .select(
                `
              id,
              title,
              slug,
              description,
              short_description,
              created_at,
              course_categories(name, slug)
            `,
              )
              .or(
                `title.ilike.%${query}%,description.ilike.%${query}%,short_description.ilike.%${query}%`,
              )
              // .eq('is_active', true) // Commented out as column may not exist
              .order("created_at", { ascending: false })
              .limit(limit)
              .abortSignal(signal),
          { cacheKey: `course_search_basic_${query}_${limit}` },
        );

        if (error) throw error;

        return (data || []).map((course: any) => ({
          id: course.id,
          title: course.title,
          description: course.short_description || course.description,
          url: `/course/${course.slug}`,
          type: "Course" as const,
          created_at: course.created_at,
          slug: course.slug,
        }));
      }
    } catch (error) {
      console.error("Course search error:", error);
      return [];
    }
  }

  /**
   * Search prompts using PostgreSQL full-text search
   */
  static async searchPrompts(
    query: string,
    options: { limit?: number } = {},
  ): Promise<SearchResult[]> {
    const { limit = 15 } = options; // Increased default limit from 2 to 15

    try {
      // Use basic ILIKE search since full-text search columns don't exist
      const { data, error } = await enhancedQuery(
        async (signal) =>
          supabase
            .from("prompts")
            .select(
              `
            id,
            title,
            description,
            content,
            created_at,
            prompt_categories(name, slug)
          `,
            )
            .or(
              `title.ilike.%${query}%,description.ilike.%${query}%,content.ilike.%${query}%`,
            )
            .eq("is_published", true)
            .order("created_at", { ascending: false })
            .limit(limit)
            .abortSignal(signal),
        { cacheKey: `prompt_search_${query}_${limit}` },
      );

      if (error) throw error;

      return (data || []).map((prompt: any) => ({
        id: prompt.id,
        title: prompt.title,
        description: prompt.description,
        url: `/prompt-library?prompt=${prompt.id}`,
        type: "Prompt" as const,
        created_at: prompt.created_at,
      }));
    } catch (error) {
      console.error("Prompt search error:", error);
      return [];
    }
  }

  /**
   * Get search suggestions based on popular searches
   */
  static async getSearchSuggestions(query: string): Promise<string[]> {
    if (query.length < 2) return [];

    try {
      // Get suggestions from recent blog post titles
      const { data, error } = await enhancedQuery(
        async (signal) =>
          supabase
            .from("blog_posts")
            .select("title")
            .ilike("title", `%${query}%`)
            .eq("is_published", true)
            .order("published_at", { ascending: false })
            .limit(5)
            .abortSignal(signal),
        { cacheKey: `search_suggestions_${query}` },
      );

      if (error) throw error;

      return (data || []).map((post: any) => post.title);
    } catch (error) {
      console.error("Search suggestions error:", error);
      return [];
    }
  }

  /**
   * Get trending search terms (based on recent blog post titles)
   */
  static async getTrendingSearches(): Promise<string[]> {
    try {
      const { data, error } = await enhancedQuery(
        async (signal) =>
          supabase
            .from("blog_posts")
            .select("title")
            .eq("is_published", true)
            .order("published_at", { ascending: false })
            .limit(10)
            .abortSignal(signal),
        { cacheKey: "trending_searches" },
      );

      if (error) throw error;

      // Extract common words from titles
      const words = (data || [])
        .map((post: any) => post.title.toLowerCase())
        .join(" ")
        .split(/\s+/)
        .filter((word: string) => word.length > 3)
        .filter(
          (word: string) =>
            ![
              "the",
              "and",
              "for",
              "with",
              "that",
              "this",
              "have",
              "from",
            ].includes(word),
        );

      // Count word frequency
      const wordCount: { [key: string]: number } = {};
      words.forEach((word: string) => {
        wordCount[word] = (wordCount[word] || 0) + 1;
      });

      // Return top 5 trending words
      return Object.entries(wordCount)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([word]) => word);
    } catch (error) {
      console.error("Trending searches error:", error);
      return [];
    }
  }

  /**
   * Highlight search terms in text
   */
  static highlightSearchTerms(text: string, query: string): string {
    if (!text || !query) return text;

    const queryWords = query.toLowerCase().split(/\s+/);
    let highlightedText = text;

    queryWords.forEach((word) => {
      if (word.length > 2) {
        const regex = new RegExp(`(${word})`, "gi");
        highlightedText = highlightedText.replace(regex, "<mark>$1</mark>");
      }
    });

    return highlightedText;
  }

  /**
   * Get search analytics (for future implementation)
   */
  static async logSearchQuery(
    query: string,
    resultsCount: number,
  ): Promise<void> {
    try {
      // This could be implemented to track search analytics
      console.log("Search analytics:", {
        query,
        resultsCount,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error logging search query:", error);
    }
  }
}

// Export convenience functions
export const searchAll = SearchService.searchAll.bind(SearchService);
export const searchBlogPosts =
  SearchService.searchBlogPosts.bind(SearchService);
export const searchCourses = SearchService.searchCourses.bind(SearchService);
export const searchPrompts = SearchService.searchPrompts.bind(SearchService);
export const getSearchSuggestions =
  SearchService.getSearchSuggestions.bind(SearchService);
export const getTrendingSearches =
  SearchService.getTrendingSearches.bind(SearchService);
export const highlightSearchTerms =
  SearchService.highlightSearchTerms.bind(SearchService);
